import { makeStyles } from "tss-react/mui"
import { Grid, IconButton, Popover, Tooltip, Typography, Box, Stack } from "@mui/material"
import { faCircleDollar, faHourglass, faMug, faThumbsUp } from "@fortawesome/pro-light-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { MerchantHomeQuery, fetchDashboardNotifications } from "../../../modules/merchant/merchant/home/merchantHomeApi"
import { useMerchant } from "../../../modules/merchants/merchantHooks"
import { useApiQuery } from "../../../core/api/useApiQuery"
import { OrderStatusType } from "../../../modules/merchant/merchant/home/merchantHomeTypes"
import { getOrdersByType } from "../../../modules/merchant/merchant/production/pages/MerchantProductionMeetingService"
import { DashboardNotifications } from "../../../modules/merchant/merchant/production/meetingService/meetingServiceTypes"
import { useState } from "react"
import { MERCHANT_PRODUCTION_MEETING_SERVICE_ROUTE } from "../../../constants/routes"
import { useNavigate } from "react-router-dom"
import { LocalizedStrict } from "../../../modules/localization/components/AppLocalized"

type StyleProps = {
    isLargeAmount?: boolean
}

const useStyles = makeStyles<StyleProps>()((theme, { isLargeAmount } = {}) => ({
    button: {
        color: theme.palette.tailwind.neutral[500],
        position: "relative",
    },
    counterBox: {
        background: "#2196F3",
        borderRadius: "6.25rem",
        color: theme.palette.common.white,
        padding: "0rem 0.406rem",
        fontSize: "0.7rem",
        lineHeight: "1rem",
        letterSpacing: "0.009rem",
        textAlign: "center",
        fontWeight: 500,
    },
    counter: {
        position: "absolute",
        top: "0rem",
        left: "1.75rem",
    },
    popoverContainer: {
        padding: "2rem",
        gap: "0.5rem",
    },
    statusListItem: {
        padding: "0.75rem",
        flexDirection: "row",
        alignItems: "start",
        gap: "0.625rem",
        minWidth: "20rem",
        maxWidth: "20rem",
        cursor: "pointer",
    },
    statusItemIconContainer: {
        position: "relative",
        background: theme.palette.tailwind.amber[50],
        color: theme.palette.tailwind.amber[700],
        border: `1px solid ${theme.palette.tailwind.amber[300]}`,
        borderRadius: "0.5rem",
        minHeight: "2.5rem",
        minWidth: "2.5rem",
        justifyContent: "center",
        alignItems: "center",
    },
    statusItemIconContainerSlate: {
        color: theme.palette.tailwind.slate[700],
        background: theme.palette.tailwind.slate[50],
        border: `1px solid ${theme.palette.tailwind.slate[300]}`,
    },
    statusItemIconContainerGreen: {
        color: theme.palette.tailwind.green[700],
        background: theme.palette.tailwind.green[50],
        border: `1px solid ${theme.palette.tailwind.green[300]}`,
    },
    statusItemTitle: {
        fontWeight: 600,
        lineHeight: 1,
    },
    rightColumn: {
        gap: "0.375rem",
    },
}))

export function AwaitingApprovalNavItem() {
    const { merchantId } = useMerchant()
    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null)
    const navigate = useNavigate()

    const dashboardNotifications = useApiQuery(fetchDashboardNotifications, {
        queryName: MerchantHomeQuery.MEETING_SERVICE_ORDER_SUMMARY_STATUS,
        dependencies: {
            merchantId,
            params: { status: OrderStatusType.READY_FOR_INVOICE.toUpperCase() },
        },
    })

    const toBeBookedAmount = getOrdersByType(
        DashboardNotifications.TO_BE_BOOKED,
        dashboardNotifications.data?.notifications,
    )
    const awaitingForApprovalAmount = getOrdersByType(
        DashboardNotifications.NOT_APPROVED,
        dashboardNotifications.data?.notifications,
    )
    const todaysMeetingsService = getOrdersByType(
        DashboardNotifications.TODAYS_MEETING_SERVICE_ORDERS,
        dashboardNotifications.data?.notifications,
    )

    const totalAmount = awaitingForApprovalAmount + toBeBookedAmount
    const isLargeAmount = totalAmount > 99
    const open = Boolean(anchorEl)
    const id = open ? "simple-popover" : undefined
    const { classes, cx } = useStyles({ isLargeAmount })

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => setAnchorEl(event.currentTarget)
    const handleClose = () => setAnchorEl(null)

    const handleItemClick =
        (tabId: number = 0) =>
        () => {
            handleClose()
            navigate(`${MERCHANT_PRODUCTION_MEETING_SERVICE_ROUTE}?tab=${tabId}`)
        }

    return (
        <Box>
            <Popover
                id={id}
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "right",
                }}
                transformOrigin={{
                    vertical: "top",
                    horizontal: "right",
                }}
            >
                <Stack className={classes.popoverContainer}>
                    <Stack className={classes.statusListItem} onClick={handleItemClick(0)}>
                        <Stack className={cx(classes.statusItemIconContainer, classes.statusItemIconContainerGreen)}>
                            <FontAwesomeIcon icon={faThumbsUp} />
                        </Stack>
                        <Stack className={classes.rightColumn}>
                            <Stack direction="row" gap={1}>
                                <LocalizedStrict id="awaiting-approval-popover-todays-orders-item-title">
                                    <Typography className={classes.statusItemTitle}>Todays orders</Typography>
                                </LocalizedStrict>
                                <Box className={classes.counterBox}>{todaysMeetingsService}</Box>
                            </Stack>
                            <LocalizedStrict id="awaiting-approval-popover-todays-orders-item-description">
                                <Typography variant="body2" color="text.secondary">
                                    Meeting service orders for delivery today
                                </Typography>
                            </LocalizedStrict>
                        </Stack>
                    </Stack>
                    <Stack className={classes.statusListItem} onClick={handleItemClick(1)}>
                        <Stack className={classes.statusItemIconContainer}>
                            <FontAwesomeIcon icon={faHourglass} />
                        </Stack>
                        <Stack className={classes.rightColumn}>
                            <Stack direction="row" gap={1}>
                                <LocalizedStrict id="awaiting-approval-popover-not-approved-item-title">
                                    <Typography className={classes.statusItemTitle}>Awaiting approval</Typography>
                                </LocalizedStrict>
                                <Box className={classes.counterBox}>{awaitingForApprovalAmount}</Box>
                            </Stack>
                            <LocalizedStrict id="awaiting-approval-popover-not-approved-item-description">
                                <Typography variant="body2" color="text.secondary">
                                    Orders that need to be approved
                                </Typography>
                            </LocalizedStrict>
                        </Stack>
                    </Stack>
                    <Stack className={classes.statusListItem} onClick={handleItemClick(2)}>
                        <Stack className={cx(classes.statusItemIconContainer, classes.statusItemIconContainerSlate)}>
                            <FontAwesomeIcon icon={faCircleDollar} />
                        </Stack>
                        <Stack className={classes.rightColumn}>
                            <Stack direction="row" gap={1}>
                                <LocalizedStrict id="awaiting-approval-popover-to-be-booked-item-title">
                                    <Typography className={classes.statusItemTitle}>To be invoiced</Typography>
                                </LocalizedStrict>
                                <Box className={classes.counterBox}>{toBeBookedAmount}</Box>
                            </Stack>
                            <LocalizedStrict id="awaiting-approval-popover-to-be-booked-item-description">
                                <Typography variant="body2" color="text.secondary">
                                    Meeting service orders that have been bookable for 3+ days
                                </Typography>
                            </LocalizedStrict>
                        </Stack>
                    </Stack>
                </Stack>
            </Popover>

            <LocalizedStrict id="awaiting-approval-popover-tooltip" attrs={{ title: true }}>
                <Tooltip title="Meeting service notifications">
                    <IconButton size="medium" onClick={handleClick} className={classes.button}>
                        <FontAwesomeIcon icon={faMug} />
                        <Grid className={cx(classes.counter, classes.counterBox)}>
                            {isLargeAmount ? "99+" : totalAmount || 0}
                        </Grid>
                    </IconButton>
                </Tooltip>
            </LocalizedStrict>
        </Box>
    )
}
